import React, { useState, useRef, useCallback } from "react";
import { Form } from "react-bootstrap";
import {
  FaDownload,
  FaPlus,
  FaCheck,
  FaTimes,
  FaTrashAlt,
  FaEdit,
  FaUpload,
} from "react-icons/fa";
import { toast } from "react-toastify";

const ZipCodeManager = React.memo(
  ({
    zipCodes,
    zipCodeKey,
    setZipCodeKey,
    selectedCountyName,
    taxState,
    onAddZipCode,
    onUpdateZipCode,
    onDeleteZipCode,
    onDownloadTemplate,
    onFileUpload,
    isLoading,
    styles,
  }) => {
    const [zipCodeInputValue, setZipCodeInputValue] = useState("");
    const [isAddingZipCode, setIsAddingZipCode] = useState(false);
    const [isEditingZipCode, setIsEditingZipCode] = useState(false);

    const fileInputRef = useRef(null);

    const handleZipCodeDropdownChange = useCallback(
      (e) => {
        setZipCodeKey(e.target.value);
        setIsAddingZipCode(false);
        setIsEditingZipCode(false);
      },
      [setZipCodeKey],
    );

    const handleStartAdd = useCallback(() => {
      setIsAddingZipCode(true);
      setIsEditingZipCode(false);
      setZipCodeInputValue("");
    }, []);

    const handleStartEdit = useCallback(() => {
      if (!zipCodeKey) return;

      const selectedZip = zipCodes.find(
        (z) => String(z.code) === String(zipCodeKey),
      );

      if (!selectedZip) return;

      setIsEditingZipCode(true);
      setIsAddingZipCode(false);
      setZipCodeInputValue(String(selectedZip.code));
    }, [zipCodes, zipCodeKey]);

    const handleCancel = useCallback(() => {
      setIsAddingZipCode(false);
      setIsEditingZipCode(false);
      setZipCodeInputValue("");
    }, []);

    const handleSaveZipCode = useCallback(() => {
      if (!zipCodeInputValue.trim()) {
        toast.error("Zip code is required");
        return;
      }

      if (isAddingZipCode) {
        if (zipCodes.some((z) => String(z.code) === zipCodeInputValue)) {
          toast.warning("Zip code already exists");
          return;
        }

        onAddZipCode({ code: zipCodeInputValue, percentage: 0 }, () => {
          setIsAddingZipCode(false);
          setZipCodeInputValue("");
        });
      }

      if (isEditingZipCode) {
        onUpdateZipCode(
          {
            zipCode: zipCodeKey,
            newZipCode: zipCodeInputValue,
          },
          () => {
            setIsEditingZipCode(false);
            setZipCodeKey(zipCodeInputValue);
            setZipCodeInputValue("");
          },
        );
      }
    }, [
      zipCodeInputValue,
      isAddingZipCode,
      isEditingZipCode,
      zipCodes,
      zipCodeKey,
      onAddZipCode,
      onUpdateZipCode,
      setZipCodeKey,
    ]);

    const handleDelete = useCallback(() => {
      if (!zipCodeKey) return;

      if (window.confirm(`Delete zip code ${zipCodeKey}?`)) {
        onDeleteZipCode(zipCodeKey, () => {
          setZipCodeKey("");
        });
      }
    }, [zipCodeKey, onDeleteZipCode, setZipCodeKey]);

    return (
      <div className={styles.formGroup}>
        <label className={styles.label}>Zip Codes ({zipCodes.length})</label>

        <div className={styles.inputWithButton}>
          {isAddingZipCode || isEditingZipCode ? (
            <>
              <div className={styles.flex1}>
                <Form.Control
                  type="text"
                  value={zipCodeInputValue}
                  onChange={(e) => setZipCodeInputValue(e.target.value)}
                  className={styles.input}
                  placeholder="Zip code"
                  disabled={isLoading}
                  autoFocus
                />
              </div>

              <div className={styles.buttonGroup}>
                <button
                  className={styles.buttonSuccess}
                  onClick={handleSaveZipCode}
                  disabled={isLoading || !zipCodeInputValue.trim()}
                >
                  <FaCheck />
                </button>

                <button className={styles.buttonWarning} onClick={handleCancel}>
                  <FaTimes />
                </button>
              </div>
            </>
          ) : (
            <>
              <div className={styles.flex1}>
                <Form.Control
                  as="select"
                  value={zipCodeKey || ""}
                  onChange={handleZipCodeDropdownChange}
                  className={styles.select}
                  disabled={!selectedCountyName || isLoading}
                >
                  <option value="">View zip codes...</option>
                  {zipCodes.map((zip) => (
                    <option key={zip.code} value={String(zip.code)}>
                      {zip.code}
                    </option>
                  ))}
                </Form.Control>
              </div>

              <div className={styles.buttonGroup}>
                <button
                  className={styles.buttonPrimary}
                  onClick={handleStartAdd}
                  disabled={!selectedCountyName || isLoading}
                >
                  <FaPlus />
                </button>

                {zipCodeKey && (
                  <>
                    <button
                      className={styles.buttonOutline}
                      onClick={handleStartEdit}
                      disabled={isLoading}
                    >
                      <FaEdit />
                    </button>

                    <button
                      className={styles.buttonDanger}
                      onClick={handleDelete}
                      disabled={isLoading}
                    >
                      <FaTrashAlt />
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {!isAddingZipCode && !isEditingZipCode && (
          <div className={`${styles.buttonGroup} ${styles.mt3}`}>
            <button
              className={styles.buttonOutline}
              onClick={() => fileInputRef.current.click()}
              disabled={!taxState || isLoading}
            >
              <FaUpload className={styles.mr2} /> Upload New
            </button>

            <button
              className={`${styles.buttonOutline} ${styles.iconButton}`}
              onClick={onDownloadTemplate}
            >
              <FaDownload />
            </button>
          </div>
        )}

        <input
          type="file"
          ref={fileInputRef}
          className={styles.hidden}
          onChange={onFileUpload}
          accept=".xlsx,.xls"
        />
      </div>
    );
  },
);

export default ZipCodeManager;
